import React, { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Chip, Label, Select, Text, TextField } from "@telia/teddy";
import { RootState } from "../../../../../../models";
import { isSupportChatOpenNow, SUPPORT_CHAT_HOURS_TEXT } from "../../../supportChatAvailability";
import { getSalesAgent, getSelectedAgent } from "../../../state/selectors/selectors";
import SupportChatPanel from "../SupportChatPanel/SupportChatPanel";
import "./ChatFormPanel.less";
import { getOrderRef } from "../../../../app/state/selectors";
import { API_URL_SEND_CHAT_TO_AWS } from "../../../api";
import { retailerFetch } from "../../../retailerFetch";

const CHAT_CATEGORIES = [
    "Innlogging",
    "Eksisterende kunde",
    "Ny kunde",
    "Kredittsjekk",
    "Ukjent feilmelding",
    "Annet",
] as const;

type ChatCategory = (typeof CHAT_CATEGORIES)[number];

const CUSTOMER_TYPES = ["Privat", "Bedrift"] as const;

type CustomerType = (typeof CUSTOMER_TYPES)[number];

const FORHANDLERSERVICE_URL = "https://www.telia.no/forhandlerservice";

const MAX_CHAT_MESSAGE_LENGTH = 500;

interface Props {
    onOpenChange: (open: boolean) => void;
    open: boolean;
}

const ChatFormPanel: FC<Props> = ({ open, onOpenChange }) => {
    const [customerType, setCustomerType] = useState<CustomerType>("Privat");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [category, setCategory] = useState<ChatCategory | "">("");
    const [chatMessage, setChatMessage] = useState("");
    const [manualAgentName, setManualAgentName] = useState("");
    const [manualDealerCode, setManualDealerCode] = useState("");
    const messageInputRef = useRef<HTMLTextAreaElement | null>(null);

    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const selectedAgent = useSelector((state: RootState) => getSelectedAgent(state));
    const effectiveAgent = selectedAgent?.firstName || selectedAgent?.lastName ? selectedAgent : salesAgent;
    const hasAgentName = !!(effectiveAgent?.firstName || effectiveAgent?.lastName);
    const isB2B = customerType === "Bedrift";
    const [isChatAvailable, setIsChatAvailable] = useState(() => isSupportChatOpenNow());
    const [chatOpen, setChatOpen] = useState(false);
    const orderRef = useSelector((state: RootState) => getOrderRef(state));

    const dialogRef = useRef<HTMLDivElement | null>(null);
    const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (open) {
            const activeElement = document.activeElement;
            if (activeElement instanceof HTMLElement) {
                previouslyFocusedElementRef.current = activeElement;
            }
            if (dialogRef.current) {
                dialogRef.current.focus();
            }
        } else if (previouslyFocusedElementRef.current) {
            const elementToFocus = previouslyFocusedElementRef.current;
            if (document.contains(elementToFocus)) {
                elementToFocus.focus();
            }
            previouslyFocusedElementRef.current = null;
        }
    }, [open]);

    useEffect(() => {
        const updateChatAvailability = () => {
            setIsChatAvailable(isSupportChatOpenNow());
        };

        const intervalId = window.setInterval(updateChatAvailability, 60_000);

        window.addEventListener("focus", updateChatAvailability);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener("focus", updateChatAvailability);
        };
    }, []);

    useEffect(() => {
        if (!category) {
            return;
        }

        const focusTimeoutId = window.setTimeout(() => {
            messageInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(focusTimeoutId);
    }, [category]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
            event.stopPropagation();
            onOpenChange(false);
            return;
        }

        if (event.key === "Tab" && dialogRef.current) {
            const focusableSelectors =
                'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
            const focusableElements = Array.from(
                dialogRef.current.querySelectorAll<HTMLElement>(focusableSelectors),
            ).filter((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true");

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

            if (event.shiftKey) {
                if (document.activeElement === firstElement || currentIndex === -1) {
                    event.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement || currentIndex === -1) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        }
    };

    const handleStartChat = () => {
        retailerFetch(API_URL_SEND_CHAT_TO_AWS, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                category,
                chatMessage,
                orderRef,
                agent: effectiveAgent,
            }),
        }).catch(() => {});

        onOpenChange(false);
        setChatOpen(true);
    };

    const handleGoToForhandlerservice = () => {
        const dealerCode = hasAgentName ? salesAgent?.dealerCode : manualDealerCode.trim() || salesAgent?.dealerCode;
        const url = new URL(FORHANDLERSERVICE_URL);
        if (dealerCode) {
            url.searchParams.set("dealerCode", dealerCode);
        }
        window.open(url.toString(), "_blank", "noopener,noreferrer");
        onOpenChange(false);
    };

    const handleSessionEnded = () => {
        setCustomerType("Privat");
        setPhoneNumber("");
        setCategory("");
        setChatMessage("");
        setManualAgentName("");
        setManualDealerCode("");
    };

    return (
        <>
            {open && (
                <div
                    className="chat-form-panel"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="chat-form-panel-title"
                    ref={dialogRef}
                    tabIndex={-1}
                    onKeyDown={handleKeyDown}
                >
                    <div className="chat-form-panel__header">
                        <div className="chat-form-panel__heading">
                            <Text
                                id="chat-form-panel-title"
                                variant="paragraph-100"
                                as="span"
                                style={{ fontWeight: 600 }}
                            >
                                Chat med support
                            </Text>
                            <Text as="p" variant="paragraph-100" className="chat-form-panel__hours">
                                ({SUPPORT_CHAT_HOURS_TEXT})
                            </Text>
                        </div>
                        <button
                            className="chat-form-panel__close"
                            onClick={() => onOpenChange(false)}
                            aria-label="lukk"
                            type="button"
                        >
                            ✕
                        </button>
                    </div>
                    <div className="chat-form-panel__body">
                        {!hasAgentName && (
                            <>
                                {!isB2B && (
                                    <Box mb="300">
                                        <TextField>
                                            <TextField.Label>Ditt navn</TextField.Label>
                                            <TextField.Input
                                                type="text"
                                                value={manualAgentName}
                                                onChange={(e) => setManualAgentName(e.target.value.slice(0, 100))}
                                                placeholder="F.eks. Ola Nordmann"
                                                autoComplete="name"
                                            />
                                        </TextField>
                                    </Box>
                                )}
                                <Box mb="300">
                                    <TextField>
                                        <TextField.Label>Forhandlerkode</TextField.Label>
                                        <TextField.Input
                                            type="text"
                                            value={manualDealerCode}
                                            onChange={(e) => setManualDealerCode(e.target.value.slice(0, 10))}
                                            placeholder="F.eks. AB123"
                                        />
                                    </TextField>
                                </Box>
                            </>
                        )}
                        <Box mb="300">
                            <Label id="chat-customer-type-label">Type kunde</Label>
                            <Chip
                                mt="50"
                                type="single"
                                variant="filter"
                                value={customerType}
                                onValueChange={(value: string) => {
                                    if (value) {
                                        setCustomerType(value as CustomerType);
                                    }
                                }}
                                aria-labelledby="chat-customer-type-label"
                            >
                                {CUSTOMER_TYPES.map((type) => (
                                    <Chip.Item key={type} value={type}>
                                        {type}
                                    </Chip.Item>
                                ))}
                            </Chip>
                        </Box>
                        {isB2B ? (
                            <>
                                <Box mb="300">
                                    <Text as="p" variant="paragraph-100">
                                        For bedriftschat, gå til forhandlerservice og velg "Bedrift: Chat".
                                    </Text>
                                </Box>
                                <Button variant="primary" fullWidth onClick={handleGoToForhandlerservice}>
                                    Gå til forhandlerservice
                                </Button>
                            </>
                        ) : (
                            <>
                                <Box mb="300">
                                    <Select
                                        label="Hva gjelder henvendelsen?"
                                        name="chat-category"
                                        value={category}
                                        onValueChange={(value) => {
                                            setCategory(value as ChatCategory);
                                            return value;
                                        }}
                                        placeholder="Velg kategori..."
                                        options={[...CHAT_CATEGORIES]}
                                    >
                                        <Select.Trigger>
                                            <Select.Value placeholder="Velg kategori..." />
                                        </Select.Trigger>
                                        <Select.Content style={{ zIndex: 10000 }}>
                                            <Select.Viewport>
                                                {CHAT_CATEGORIES.map((cat) => (
                                                    <Select.Item key={cat} value={cat}>
                                                        {cat}
                                                    </Select.Item>
                                                ))}
                                            </Select.Viewport>
                                        </Select.Content>
                                    </Select>
                                </Box>
                                <Box mb="300">
                                    <TextField>
                                        <TextField.Label>Melding (valgfritt)</TextField.Label>
                                        <TextField.Textarea
                                            ref={messageInputRef}
                                            value={chatMessage}
                                            onChange={(e) =>
                                                setChatMessage(e.target.value.slice(0, MAX_CHAT_MESSAGE_LENGTH))
                                            }
                                            maxLength={MAX_CHAT_MESSAGE_LENGTH}
                                            placeholder="Beskriv kort hva henvendelsen gjelder..."
                                            rows={4}
                                        />
                                    </TextField>
                                </Box>
                                <Box mb="300">
                                    <TextField>
                                        <TextField.Label>Telefonnummer (valgfritt)</TextField.Label>
                                        <TextField.Input
                                            type="tel"
                                            value={phoneNumber}
                                            onChange={(e) => setPhoneNumber(e.target.value.slice(0, 12))}
                                            placeholder="F.eks. 98765432"
                                            autoComplete="tel"
                                        />
                                    </TextField>
                                </Box>
                                {!isChatAvailable && (
                                    <Box mb="200">
                                        <Text as="p" variant="paragraph-100">
                                            Chatten er stengt akkurat nå.
                                        </Text>
                                    </Box>
                                )}
                                <Button
                                    variant="primary"
                                    fullWidth
                                    onClick={handleStartChat}
                                    disabled={!isChatAvailable}
                                >
                                    Start chat
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}
            <SupportChatPanel
                open={chatOpen}
                onOpenChange={setChatOpen}
                onSessionEnded={handleSessionEnded}
                phoneNumber={phoneNumber.trim().slice(0, 12) || undefined}
                initialMessage={category || undefined}
                chatMessage={chatMessage.trim() || undefined}
                manualAgentName={!hasAgentName ? manualAgentName.trim().slice(0, 100) || undefined : undefined}
                manualDealerCode={!hasAgentName ? manualDealerCode.trim().slice(0, 10) || undefined : undefined}
            />
        </>
    );
};

export default ChatFormPanel;
